import { useState, useCallback, useMemo, useEffect } from "react";
import { Table, TableBody } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { AppTableHeader } from "./AppTableHeader";
import { AppTableRow } from "./AppTableRow";
import { AppCatalogHeader } from "./AppCatalogHeader";
import { AppCatalogEmpty } from "./AppCatalogEmpty";
import { AppCatalogLoading } from "./AppCatalogLoading";
import { InfiniteScrollTable } from "./InfiniteScrollTable";
import { useAppCatalogActions } from "./AppCatalogActions";
import { useConvexInfiniteApps } from "@/hooks/useConvexInfiniteApps";
import { useAppTotalCount } from "@/hooks/useAppTotalCount";
import { App } from "@/types/app";

interface AppCatalogTableProps {
  searchTerm: string;
  selectedCategory: string;
  systemAppFilter: "all" | "system" | "non-system";
  selectedTags?: string[];
  onAppSelect: (app: App | null) => void;
  onBulkImport?: () => void;
  selectedApp?: App | null;
}

export const AppCatalogTable = ({
  searchTerm,
  selectedCategory,
  systemAppFilter,
  selectedTags = [],
  onAppSelect,
  selectedApp,
}: AppCatalogTableProps) => {
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const { toast } = useToast();

  // Use the custom hook for infinite scrolling with Convex
  const { apps, isLoading, isLoadingMore, hasMore, loadMore } = useConvexInfiniteApps({
    searchTerm: searchTerm || undefined,
    pageSize: 50,
  });
  
  const { totalCount } = useAppTotalCount();

  // Reset selected apps when search/filters change (useEffect, not useMemo)
  useEffect(() => {
    setSelectedAppIds([]);
  }, [searchTerm, selectedCategory, systemAppFilter, selectedTags]);

  // Filter apps on the client side
  const filteredApps = useMemo(() => {
    return apps.filter((app: any) => {
      // Category filter
      if (selectedCategory && selectedCategory !== "all" && app.category !== selectedCategory) {
        return false;
      }

      // System app filter - using the correct Convex field name
      if (systemAppFilter === "system" && !app.system) {
        return false;
      }
      if (systemAppFilter === "non-system" && app.system) {
        return false;
      }

      // Tags filter
      if (selectedTags.length > 0) {
        const appTags = app.tags || [];
        const hasMatchingTag = selectedTags.some((tag) => appTags.includes(tag));
        if (!hasMatchingTag) {
          return false;
        }
      }

      return true;
    });
  }, [apps, selectedCategory, systemAppFilter, selectedTags]);

  const totalAppsCount = filteredApps.length;

  // Transform Convex app data to match our App interface
  const transformedApps: App[] = filteredApps.map(
    (app: any) =>
      ({
        id: app.id,
        bundle_id: app.bundleId,
        app_name: app.name,
        app_icon_url: app.iconUrl,
        version_code: app.version,
        category: app.category,
        tags: app.tags || [],
        tag_ids: app.tagIds || [],
        description: app.description,
        created_at: app._creationTime ? new Date(app._creationTime).toISOString() : new Date().toISOString(),
        updated_at: app._creationTime ? new Date(app._creationTime).toISOString() : new Date().toISOString(),
        system_app: app.system || false, // Map from Convex 'system' field
        app_store_id: app.storeId,
        // Add the system field for the Type column display
        system: app.system || false, // Use the correct Convex field name
      } as App & { system: boolean })
  );

  const { isDeleting, handleBulkDelete, handleDeleteApp } = useAppCatalogActions({
    selectedAppIds,
    onSelectionChange: setSelectedAppIds,
    onRefresh: () => {
      // Convex handles refetching automatically
    },
  });

  const handleAppClick = (app: App) => {
    onAppSelect(app);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAppIds(transformedApps.map((app) => app.id));
    } else {
      setSelectedAppIds([]);
    }
  };

  const handleSelectApp = (appId: string, checked: boolean) => {
    if (checked) {
      setSelectedAppIds((prev) => [...prev, appId]);
    } else {
      setSelectedAppIds((prev) => prev.filter((id) => id !== appId));
    }
  };

  const handleDeleteAppFromSidebar = async (appId: string) => {
    await handleDeleteApp(appId);
    onAppSelect(null);
  };

  const handleLoadMore = useCallback(() => {
    loadMore();
  }, [loadMore]);

  const isAllSelected = transformedApps.length > 0 && selectedAppIds.length === transformedApps.length;
  const isSomeSelected = selectedAppIds.length > 0 && selectedAppIds.length < transformedApps.length;

  // Show loading while initial query is loading
  if (isLoading) {
    return <AppCatalogLoading />;
  }

  if (transformedApps.length === 0 && !hasMore) {
    return <AppCatalogEmpty searchTerm={searchTerm} />;
  }

  return (
    /* Anchors the component to the viewport height; prevents page from extending */
    <div className="flex flex-col h-screen overflow-hidden">
      <AppCatalogHeader
        selectedCount={selectedAppIds.length}
        selectedApps={transformedApps.filter((app) => selectedAppIds.includes(app.id))}
        onBulkDelete={handleBulkDelete}
        onBulkEditSuccess={() => {
          setSelectedAppIds([]);
        }}
        isDeleting={isDeleting}
      />

      {/* Apps count display */}
      <div className="mb-2">
        <p className="text-sm text-muted-foreground">
          Showing {totalAppsCount} of {totalCount} apps
          {hasMore ? " (scroll for more)" : ""}
        </p>
      </div>

      {/* Only this region scrolls; bottom stays stuck to viewport bottom */}
      <div className="min-h-0 flex-1 overflow-auto">
        <InfiniteScrollTable
          hasMore={hasMore}
          isLoading={isLoadingMore}
          onLoadMore={handleLoadMore}
          stickyHeader={
            <Table>
              <AppTableHeader
                showSelect={true}
                isAllSelected={isAllSelected}
                isSomeSelected={isSomeSelected}
                onSelectAll={handleSelectAll}
              />
            </Table>
          }
        >
          <Table>
            <TableBody>
              {transformedApps.map((app) => (
                <AppTableRow
                  key={app.id}
                  app={app}
                  isSelected={selectedAppIds.includes(app.id)}
                  onSelect={(checked) => handleSelectApp(app.id, checked)}
                  onClick={handleAppClick}
                  showSelect={true}
                />
              ))}
            </TableBody>
          </Table>
        </InfiniteScrollTable>
      </div>
    </div>
  );
};
